<script setup lang="ts">
import { useSlots, computed } from 'vue'
import { useDisplay } from 'vuetify'

const slots = useSlots()
const hasCustomMenuSlot = computed(() => !!slots['menu-slot'])

const props = defineProps<{
  id?: string
  disabled?: boolean
}>()

const { mdAndDown } = useDisplay()
const isMobile = computed(() => mdAndDown.value)
</script>

<template>
  <div class="position-relative">
    <v-menu
      v-if="!hasCustomMenuSlot && !props.disabled"
      :transition="isMobile ? 'dialog-bottom-transition' : 'fade-transition'"
      :location="isMobile ? undefined : 'bottom end'"
      :location-strategy="isMobile ? 'static' : 'connected'"
      :close-on-content-click="true"
      :scrim="isMobile ? 'rgba(0,0,0,.4)' : false"
      :content-class="isMobile ? 'options-bottom-sheet' : ''"
      :offset="isMobile ? 0 : 8"
    >
      <template #activator="{ props: activatorProps }">
        <v-btn
          :id="props.id ? `${props.id}-menu-button` : 'menu-button'"
          v-bind="activatorProps"
          variant="text"
          elevation="0"
          :ripple="false"
          class="dots-btn ma-0 pa-0"
          aria-label="Menu Options"
          icon="mdi-dots-vertical"
          width="20px"
          height="20px"
        />
      </template>

      <v-card elevation="8" class="menu-card" color="background">
        <div v-if="isMobile" class="sheet-handle" />

        <div class="cardoptions-content">
          <slot />
        </div>
      </v-card>
    </v-menu>

    <slot name="menu-slot" />
  </div>
</template>

<style scoped>
/* =========================
   BUTTON (3 dots)
   ========================= */
.dots-btn {
  position: relative;
  z-index: 1;
  background: transparent !important;
  box-shadow: none !important;
  transition:
    background-color 0.15s ease,
    transform 0.1s ease;
}

.dots-btn::before {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  background: transparent;
  transition: background-color 0.15s ease;
  z-index: -1;
}

.dots-btn:hover::before {
  background-color: rgba(0, 0, 0, 0.08);
}

.dots-btn:focus,
.dots-btn:focus-visible {
  outline: none !important;
  box-shadow: none !important;
}

.dots-btn:active {
  transform: scale(0.98);
}

.dots-btn :deep(.v-btn__overlay),
.dots-btn :deep(.v-btn__underlay),
.dots-btn :deep(.v-ripple__container) {
  display: none !important;
}

.dots-btn :deep(.v-icon) {
  color: rgba(0, 0, 0, 0.6) !important;
}

/* =========================
   CARD (typography fixed)
   ========================= */
.menu-card {
  border-radius: 8px !important;
  overflow: hidden;
  font-family: 'Open Sans', sans-serif !important;
  font-size: 16px !important;
}

.menu-card :deep(*) {
  font-family: inherit;
}

.cardoptions-content :deep(.v-list-item-title),
.cardoptions-content :deep(.v-list-item),
.cardoptions-content :deep(.item-menu),
.cardoptions-content :deep(.item-menu-red),
.cardoptions-content :deep(.v-row .v-col) {
  font-family: 'Open Sans', sans-serif !important;
  font-size: 16px !important;
  padding: 26px;
}

/* =========================
   CONTENT spacing
   ========================= */
.cardoptions-content {
  padding: 8px 0;
}

.cardoptions-content :deep(.v-container) {
  padding: 0 !important;
}

.cardoptions-content :deep(.v-row) {
  margin: 0 !important;
  min-height: 44px;
  display: flex;
  align-items: center;
  padding: 8px 8px;
}

.cardoptions-content :deep(.v-col),
.cardoptions-content :deep([class*='v-col-']) {
  padding: 0 !important;
}

.cardoptions-content :deep(.v-row .item-menu),
.cardoptions-content :deep(.v-row .item-menu-red),
.cardoptions-content :deep(.v-row .v-col) {
  line-height: 32px;
}

.cardoptions-content :deep(.v-row:hover) {
  background: rgba(0, 0, 0, 0.04);
}

.cardoptions-content :deep(.v-list) {
  padding: 0 !important;
}

.cardoptions-content :deep(.v-list-item) {
  min-height: 32px !important;
  height: 32px !important;
  padding-left: 16px !important;
  padding-right: 16px !important;
}

.cardoptions-content :deep(.v-list-item__content) {
  padding: 0 !important;
}

.cardoptions-content :deep(.v-list-item-title) {
  line-height: 32px !important;
  margin: 0 !important;
  padding: 0 !important;
}

.cardoptions-content :deep(.v-divider) {
  margin: 4px 0 !important;
}

/* =========================
   MOBILE bottom sheet (50vh)
   ========================= */
@media (max-width: 959px) {
  :deep(.options-bottom-sheet) {
    position: fixed !important;
    inset: auto 0 0 0 !important;
    transform: none !important;
    width: 100% !important;
    background: transparent;
    display: flex !important;
    align-items: flex-end !important;
    justify-content: center !important;
    padding-bottom: env(safe-area-inset-bottom);
  }

  :deep(.v-overlay__content) {
    width: 100% !important;
    height: auto !important;
  }

  .menu-card {
    width: 100% !important;
    max-height: 50vh !important;
    height: 360px !important;
    overflow-y: auto;
    background-color: #fff !important;
    border-top-left-radius: 16px !important;
    border-top-right-radius: 16px !important;
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    box-shadow: 0 -8px 28px rgba(0, 0, 0, 0.22) !important;
  }

  .sheet-handle {
    width: 44px;
    height: 5px;
    border-radius: 9999px;
    background: rgba(0, 0, 0, 0.18);
    margin: 10px auto 8px;
  }

  .cardoptions-content {
    padding: 4px 0 12px;
  }
}
</style>
